import { useFormContext } from 'react-hook-form';
import { SearchableSelect } from '@/components/common';
import { useLiveInsurers } from '@/hooks/useInsurerPlugins';
import { useVehicleVariants } from '@/hooks/useVehicles';
import type { VehicleVariant } from '@/services/vehicles';
import type { MotorQuoteFormData } from '../../schema';

/** What `/vehicles/variants/lookup/` answers per row. `insurer_codes` (B11)
 *  lists the insurers holding a vehicle code for the variant; absent on an
 *  older response, in which case nothing is claimed either way. */
type LookupVariant = VehicleVariant & { seating_capacity?: number; insurer_codes?: string[] };

export const NO_INSURER_CODE_MARKER = 'No insurer code — live premium unavailable';

/** True only when the server said which insurers hold a code, the switched-on
 *  motor insurers are known, and none of them holds one. Never hides the
 *  variant: a hand-priced quote is still a quote. */
export function lacksLivePremiumCode(variant: LookupVariant, liveCodes: string[]): boolean {
  const held = variant.insurer_codes;
  return Array.isArray(held) && liveCodes.length > 0 && !liveCodes.some((code) => held.includes(code));
}

export function VariantStep() {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<MotorQuoteFormData>();

  const modelId = watch('model_id');
  const modelName = watch('model_name');
  const variantId = watch('variant_id');
  const { data: variants, isLoading } = useVehicleVariants(modelId);
  // The insurers the platform can price motor with right now (Country Admin switches).
  const { data: live } = useLiveInsurers('motor');
  const liveInsurers = live ?? [];
  const liveCodes = liveInsurers.map((insurer) => insurer.code);
  const liveNames = liveInsurers.map((insurer) => insurer.display_name).join(', ');

  const variantList: LookupVariant[] = variants ?? [];
  const options = variantList.map((v) => ({
    value: v.id,
    label: `${v.name} (${v.engine_cc ?? '-'}cc, ${v.fuel_type_name ?? '-'})${
      lacksLivePremiumCode(v, liveCodes) ? ` · ${NO_INSURER_CODE_MARKER}` : ''
    }`,
  }));
  const selectedVariant = variantList.find((v) => v.id === variantId) ?? null;

  const handleChange = (id: string) => {
    const variant = variantList.find((v) => v.id === id);
    setValue('variant_id', id, { shouldValidate: true, shouldDirty: true });
    setValue('variant_name', variant?.name ?? '', { shouldValidate: false });
    // Store the human-readable fuel name ("Diesel"), not the FK UUID.
    setValue('fuel_type', variant?.fuel_type_name ?? '', { shouldValidate: false });
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Variant</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
        Select the vehicle variant{modelName ? ` for ${modelName}` : ''}.
      </p>
      <div className="sm:max-w-sm">
        <SearchableSelect
          id="variant_id"
          label="Variant"
          options={options}
          value={variantId}
          onChange={handleChange}
          placeholder={isLoading ? 'Loading...' : 'Select variant'}
          error={errors.variant_id?.message}
          disabled={!modelId}
          required
        />
      </div>

      {selectedVariant && (
        <div className="mt-4 flex flex-wrap gap-2 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800 px-4 py-3">
          <span className="inline-flex items-center gap-1 text-sm text-primary-700 dark:text-primary-300">
            <span className="font-medium">Engine:</span> {selectedVariant.engine_cc}cc
          </span>
          <span className="text-primary-300 dark:text-primary-600">|</span>
          <span className="inline-flex items-center gap-1 text-sm text-primary-700 dark:text-primary-300">
            <span className="font-medium">Fuel:</span> {selectedVariant.fuel_type_name ?? '-'}
          </span>
          <span className="text-primary-300 dark:text-primary-600">|</span>
          <span className="inline-flex items-center gap-1 text-sm text-primary-700 dark:text-primary-300">
            <span className="font-medium">Seating:</span> {selectedVariant.seating_capacity ?? '-'}
          </span>
        </div>
      )}

      {selectedVariant && lacksLivePremiumCode(selectedVariant, liveCodes) && (
        <div
          role="note"
          className="mt-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 px-4 py-3 text-sm text-amber-900 dark:text-amber-200"
        >
          <p className="font-medium">{NO_INSURER_CODE_MARKER}</p>
          <p className="mt-1">
            This variant is not mapped to the vehicle master of {liveNames}, so no live premium can be
            fetched for it. You can continue and add the premium by hand, or pick the variant they list
            for this vehicle.
          </p>
        </div>
      )}
    </div>
  );
}
